import type {
  Bonus,
  Challenge,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'CONSTRUCTION SITE PRO™'
export const CHECKOUT_URL = 'https://construction-site-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '757545003709827'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/boxUuY99tqmpJvjNogcchh/Diseno-sin-titulo-16.webp'

export const inspectionFlow = ['PLAN', 'COORDINATE', 'TRACK', 'MONITOR', 'DOCUMENT', 'CONTROL']

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/jnRtywb9dAJ6P665KBrhoc/Captura-de-pantalla-2026-10-08-a-la_s_-6.04.36_p._m..png',
    alt: 'Professional Delivery Receipt & Record Log — delivery details, delivery note and receiving record',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/4CDoC94qAXgxubcYtJgkya/Captura-de-pantalla-2026-10-08-a-la_s_-6.05.00_p._m..png',
    alt: 'Construction Progress Report — photo example with photo IDs, locations, activities and observations',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/dYR9mLZHuQPtkVwHZUec6q/Captura-de-pantalla-2026-10-08-a-la_s_-6.05.13_p._m..png',
    alt: 'Document Progress With Comparable Photographs — before and after comparison with photography tips',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pPv1BF629NpVwsotYSEvNF/Captura-de-pantalla-2026-10-08-a-la_s_-6.04.47_p._m..png',
    alt: 'How to Create Professional Construction Photo Records — labeling guide, photo types and record template',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/xB2mijjS2e7ckwBTonvoLu/Captura-de-pantalla-2026-10-08-a-la_s_-6.04.19_p._m..png',
    alt: 'What to Collect Before Writing Your Daily Report — six-section verification checklist',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 TOO MANY MOVING PARTS. NOT ENOUGH ORGANIZATION.',
    body: 'Crews need instructions. Materials need tracking. Subcontractors need coordination. Work needs checking. Deadlines keep approaching. Without a structured system, managing the entire jobsite can quickly become overwhelming.',
  },
  {
    title: '⚠️ ONE MISSED DETAIL CAN DISRUPT THE ENTIRE DAY.',
    body: "A missing delivery, an unfinished task, a scheduling conflict, or a problem nobody followed up on. Small coordination issues can turn into costly delays when responsibilities, priorities, and pending actions aren't clearly documented.",
  },
  {
    title: '📚 YOUR SITE INFORMATION IS EVERYWHERE.',
    body: 'Daily notes, WhatsApp messages, spreadsheets, photographs, material records, subcontractor updates, progress reports, and unfinished checklists. The information exists—but finding and organizing everything takes valuable time.',
  },
  {
    title: '😩 STOP CREATING THE SAME PAPERWORK FROM SCRATCH.',
    body: "Daily reports, crew assignments, material logs, progress trackers, site records, and follow-up documents. You shouldn't have to rebuild your management process every time a new workday begins.",
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'DAILY SITE OPERATIONS & PLANNING™',
    body: 'Organize construction activities with ready-to-use daily planning sheets, morning briefing templates, work schedules, task priority checklists, site preparation procedures, activity sequencing tools, responsibility assignment forms, and end-of-day planning resources.',
  },
  {
    number: 2,
    title: 'CREW MANAGEMENT & WORK COORDINATION™',
    body: 'Coordinate construction crews and subcontractors with workforce allocation templates, attendance logs, trade coordination schedules, responsibility matrices, team briefing forms, labor tracking sheets, communication records, and daily crew management tools.',
  },
  {
    number: 3,
    title: 'MATERIALS, EQUIPMENT & RESOURCE CONTROL™',
    body: 'Keep construction resources organized with material inventory trackers, purchase request forms, delivery verification checklists, supplier follow-up sheets, equipment allocation records, material consumption logs, shortage trackers, and resource planning templates.',
  },
  {
    number: 4,
    title: 'PROJECT PROGRESS & QUALITY CONTROL™',
    body: 'Monitor construction progress and workmanship with milestone tracking sheets, work completion checklists, construction quality inspection forms, progress monitoring templates, defect registers, punch lists, rework records, and corrective-action tracking resources.',
  },
  {
    number: 5,
    title: 'SITE INSPECTIONS & ISSUE MANAGEMENT™',
    body: 'Document operational site conditions and outstanding problems with site walkthrough checklists, work area inspection forms, issue registers, housekeeping reviews, nonconformance reports, photographic observation records, corrective-action forms, and follow-up verification tools.',
  },
  {
    number: 6,
    title: 'DAILY REPORTS, DOCUMENTATION & SITE RECORDS™',
    body: 'Create professional construction records with daily site report templates, site diaries, weather and delay logs, workforce activity records, photographic progress reports, meeting minutes, RFI trackers, change documentation forms, and weekly construction summary templates.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'CONSTRUCTION DELAY & RECOVERY TOOLKIT™',
    body: 'Identify construction delays, document their causes, assess potential schedule impacts, compare recovery options, assign responsibilities, and organize practical recovery action plans with structured tracking and follow-up tools.',
  },
  {
    number: 2,
    title: 'CONSTRUCTION COST & EXPENSE CONTROL KIT™',
    body: 'Keep construction expenses organized with cost tracking sheets, committed-versus-actual spending records, purchase variance reviews, forecast worksheets, cost issue documentation, and weekly expense monitoring resources.',
  },
  {
    number: 3,
    title: 'SUBCONTRACTOR PERFORMANCE & ACCOUNTABILITY KIT™',
    body: 'Improve subcontractor coordination with scope alignment forms, commitment tracking logs, dependency matrices, missed-deadline reviews, performance follow-up records, and structured accountability tools.',
  },
  {
    number: 4,
    title: 'CONSTRUCTION SITE HANDOVER & CLOSEOUT KIT™',
    body: 'Organize the final stages of construction with closeout readiness checklists, outstanding work registers, document handover records, warranty and manual trackers, client walkthrough forms, and project handover templates.',
  },
  {
    number: 5,
    title: 'CONSTRUCTION PROBLEM-SOLVING PLAYBOOK™',
    body: 'Handle unexpected jobsite problems with practical decision-making frameworks, evidence collection tools, root-cause review worksheets, solution comparison matrices, action ownership trackers, and lessons-learned records.',
  },
]

export const differentiators: Differentiator[] = [
  {
    emoji: '🏗️',
    title: 'Practical construction management resources',
    body: 'Access planning templates, coordination forms, tracking sheets, checklists, professional reports, and site management tools whenever you need them.',
  },
  {
    emoji: '📋',
    title: 'Organized daily operations',
    body: 'Move from morning planning to crew coordination, material tracking, progress monitoring, issue resolution, and end-of-day documentation.',
  },
  {
    emoji: '🔧',
    title: '6 dedicated management areas',
    body: 'Daily operations, workforce coordination, materials and equipment, project progress, site inspections, and professional reporting.',
  },
  {
    emoji: '🎁',
    title: '5 practical professional bonuses',
    body: 'Delay recovery, expense control, subcontractor accountability, project handover, and construction problem-solving resources.',
  },
  {
    emoji: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to organize a workday, coordinate subcontractors, track resources, document progress, or manage outstanding construction tasks.',
  },
]

export const offerBullets = [
  '6 comprehensive construction management modules',
  '5 strategic professional bonuses',
  'Ready-to-use daily site planning templates',
  'Crew and subcontractor coordination tools',
  'Materials and equipment tracking resources',
  'Construction progress and quality checklists',
  'Professional daily reporting and documentation',
  'Delay recovery, cost control, and project handover tools',
]

export const challenges: Challenge[] = [
  {
    emoji: '🏗️',
    quote: 'Every day starts with a new list of priorities.',
    body: 'Keep daily activities, responsibilities, and upcoming work organized with practical planning resources instead of relying on scattered notes.',
  },
  {
    emoji: '📋',
    quote: 'The paperwork never seems to end.',
    body: 'Use structured daily reports, site diaries, progress records, and documentation templates instead of building every form from scratch.',
  },
  {
    emoji: '👷',
    quote: 'Keeping different crews coordinated is a constant challenge.',
    body: 'Organize assignments, subcontractor commitments, work area responsibilities, and follow-up actions with clear coordination tools.',
  },
  {
    emoji: '⚠️',
    quote: 'Small problems become bigger when nobody tracks them.',
    body: 'Record outstanding issues, assign responsibilities, document corrective actions, and keep unresolved tasks visible until they are reviewed.',
  },
  {
    emoji: '🔧',
    quote: 'I need tools I can actually use on the job.',
    body: 'Access practical construction management resources designed for recurring site activities, not just theory to read once and forget.',
  },
]

export const testimonials: Testimonial[] = [
  {
    name: 'Mike Callahan, 46',
    role: 'Site Superintendent',
    location: 'Phoenix, United States',
    headline: 'My mornings finally start with a plan.',
    quote:
      'I run a four-storey commercial build with six trades on site most days. The morning briefing template and daily planning sheet replaced the notebook I used to scribble in at the gate. Now every foreman knows their area, their priorities and who they depend on before the first truck shows up.',
  },
  {
    name: 'Gary Whitfield, 52',
    role: 'General Foreman',
    location: 'Manchester, United Kingdom',
    headline: 'The delivery log alone saved me a dispute.',
    quote:
      'We had a plasterboard drop come in short and the supplier swore it was complete. Because I had the delivery verification checklist filled in with quantities, photos and the driver’s note, the balance was sent at no cost. That one form paid for the whole system several times over.',
  },
  {
    name: 'Daniel Okafor, 38',
    role: 'Construction Site Manager',
    location: 'Toronto, Canada',
    headline: 'Subcontractors are easier to keep accountable.',
    quote:
      'Coordinating electrical, mechanical and drywall crews in the same zone used to mean constant phone calls. With the responsibility matrix and commitment tracking log, everyone can see what was promised and when. Missed deadlines get documented and followed up instead of forgotten.',
  },
  {
    name: 'Rebecca Lawson, 41',
    role: 'Construction Coordinator',
    location: 'Brisbane, Australia',
    headline: 'Daily reports take me half the time now.',
    quote:
      'I used to rebuild the daily report every evening from WhatsApp messages and photos on my phone. The site diary, photo record template and weather and delay log give me a consistent structure, so the report is basically done by the time the site closes.',
  },
  {
    name: 'Tony Russo, 49',
    role: 'Residential Building Contractor',
    location: 'Columbus, United States',
    headline: 'Practical tools, not another book on a shelf.',
    quote:
      'I run renovations and new homes with a small team, so I don’t have time for theory. The punch lists, issue register and closeout checklists are things I print and use every week. Handovers are smoother and clients can see we’re organized from day one to the final walkthrough.',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Construction site supervisors, foremen, contractors, site managers, construction coordinators, and professionals responsible for organizing daily construction operations.',
  },
  {
    q: 'What types of construction projects can I use it for?',
    a: 'The system includes adaptable management resources for residential construction, commercial building projects, renovations, fit-outs, and other construction environments.',
  },
  {
    q: 'Does it include ready-to-use templates and checklists?',
    a: 'Yes. Daily planning sheets, crew coordination forms, material trackers, progress monitoring templates, site inspection checklists, professional reports, and documentation resources are central parts of the system.',
  },
  {
    q: 'Can it help me manage subcontractors and daily activities?',
    a: 'Yes. The system includes tools for coordinating work assignments, tracking subcontractor commitments, organizing responsibilities, documenting progress, and following up on outstanding tasks.',
  },
  {
    q: 'Does it include construction reporting tools?',
    a: 'Yes. Module 6 provides daily construction reports, site diaries, progress documentation, meeting records, photographic reporting templates, and other professional site documentation resources.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. CONSTRUCTION SITE PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a practical construction site management toolkit designed to support daily organization, coordination, tracking, and documentation.',
  },
  {
    q: 'Is it another theory-heavy construction ebook?',
    a: 'No. The system focuses on practical workflows, reusable templates, checklists, tracking forms, reporting tools, and structured management resources.',
  },
  {
    q: 'Does it replace professional construction software or regulatory requirements?',
    a: 'No. It is a practical management resource, not a replacement for specialized construction software, approved project procedures, engineering assessments, contractual obligations, or applicable safety regulations.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Mike C.', city: 'Phoenix', country: 'United States' },
  { name: 'Gary W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Daniel O.', city: 'Toronto', country: 'Canada' },
  { name: 'Rebecca L.', city: 'Brisbane', country: 'Australia' },
  { name: 'Brandon H.', city: 'Atlanta', country: 'United States' },
  { name: 'Steve P.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Ryan M.', city: 'Edmonton', country: 'Canada' },
  { name: 'Jake T.', city: 'Sydney', country: 'Australia' },
  { name: 'Carlos R.', city: 'San Antonio', country: 'United States' },
  { name: 'Liam G.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Shawn K.', city: 'Ottawa', country: 'Canada' },
  { name: 'Nathan B.', city: 'Perth', country: 'Australia' },
]
